import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import {
  getActionOptions,
  getConditionOptions,
} from 'dashboard/helper/automationHelper';
import {
  MESSAGE_CONDITION_VALUES,
  PRIORITY_CONDITION_VALUES,
  TICKET_STATUS_VALUES,
  TICKET_PRIORITY_VALUES,
  TICKET_CHANNEL_VALUES,
  ORDER_TYPE_VALUES,
  ORDER_CLASS_VALUES,
} from 'dashboard/constants/automation';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';

/**
 * This is a shared composables that holds utilities used to build dropdown and file options
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export default function useAutomationValues() {
  const getters = useStoreGetters();
  const { t } = useI18n();
  const agents = useMapGetter('agents/getAgents');
  const campaigns = useMapGetter('campaigns/getAllCampaigns');
  const contacts = useMapGetter('contacts/getContacts');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabels');
  const teams = useMapGetter('teams/getTeams');
  const slaPolicies = useMapGetter('sla/getSLA');

  // Ticket categories - loaded dynamically
  const ticketCategoriesData = ref([]);

  const loadTicketCategories = async () => {
    try {
      const response = await TicketCategoriesAPI.get({ active_only: 'true' });
      ticketCategoriesData.value = response.data || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading ticket categories:', error);
    }
  };

  // Exploitation zones - loaded dynamically
  const exploitationZonesData = ref([]);

  const loadExploitationZones = async () => {
    try {
      const response = await CeaLookupsAPI.get('exploitation_zones');
      exploitationZonesData.value = response.data || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading exploitation zones:', error);
    }
  };

  // Load categories and exploitation zones on mount
  onMounted(() => {
    loadTicketCategories();
    loadExploitationZones();
  });

  const ticketCategories = computed(() =>
    ticketCategoriesData.value.map(cat => ({
      id: cat.id,
      name: cat.name,
    }))
  );

  const ticketSubcategories = computed(() => {
    const subcats = [];
    ticketCategoriesData.value.forEach(cat => {
      (cat.ticket_subcategories || []).forEach(sub => {
        subcats.push({
          id: sub.id,
          name: `${cat.name} > ${sub.name}`,
        });
      });
    });
    return subcats;
  });

  const exploitationZoneOptions = computed(() =>
    exploitationZonesData.value.map(zone => ({
      id: zone.code,
      name: `${zone.code} - ${zone.description}`,
    }))
  );

  const ticketStatusOptions = computed(() =>
    TICKET_STATUS_VALUES.map(item => ({
      id: item.id,
      name: t(`TICKETS.STATUS.${item.i18nKey}`),
    }))
  );

  const ticketPriorityOptions = computed(() =>
    TICKET_PRIORITY_VALUES.map(item => ({
      id: item.id,
      name: t(`TICKETS.PRIORITY.${item.i18nKey}`),
    }))
  );

  const ticketChannelOptions = computed(() =>
    TICKET_CHANNEL_VALUES.map(item => ({
      id: item.id,
      name: item.name,
    }))
  );

  const orderTypeOptions = computed(() =>
    ORDER_TYPE_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.ORDER_TYPE_TYPES.${item.i18nKey}`),
    }))
  );

  const orderClassOptions = computed(() =>
    ORDER_CLASS_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.ORDER_CLASS_TYPES.${item.i18nKey}`),
    }))
  );

  const booleanFilterOptions = computed(() => [
    { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
    { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
  ]);

  const statusFilterItems = computed(() => {
    return {
      open: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
      },
      resolved: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
      },
      pending: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
      },
      snoozed: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
      },
      all: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
      },
    };
  });

  const statusFilterOptions = computed(() => {
    const statusFilters = statusFilterItems.value;
    return [
      ...Object.keys(statusFilters).map(status => ({
        id: status,
        name: statusFilters[status].TEXT,
      })),
      { id: 'all', name: t('CHAT_LIST.FILTER_ALL') },
    ];
  });

  const messageTypeOptions = computed(() =>
    MESSAGE_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.MESSAGE_TYPES.${item.i18nKey}`),
    }))
  );

  const priorityOptions = computed(() =>
    PRIORITY_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.PRIORITY_TYPES.${item.i18nKey}`),
    }))
  );

  /**
   * Adds a translated "None" option to the beginning of a list
   * @param {Array} list - The list to add "None" to
   * @returns {Array} A new array with "None" option at the beginning
   */
  const addNoneToList = list => [
    {
      id: 'nil',
      name: t('AUTOMATION.NONE_OPTION') || 'None',
    },
    ...(list || []),
  ];

  /**
   * Gets the condition dropdown values for a given type.
   * @param {string} type - The type of condition.
   * @returns {Array} An array of condition dropdown values.
   */
  const getConditionDropdownValues = type => {
    return getConditionOptions({
      agents: agents.value,
      booleanFilterOptions: booleanFilterOptions.value,
      campaigns: campaigns.value,
      contacts: contacts.value,
      customAttributes: getters['attributes/getAttributes'].value,
      inboxes: inboxes.value,
      labels: labels.value,
      statusFilterOptions: statusFilterOptions.value,
      priorityOptions: priorityOptions.value,
      messageTypeOptions: messageTypeOptions.value,
      teams: teams.value,
      languages,
      countries,
      type,
      // Ticket-specific options
      ticketCategories: ticketCategories.value,
      ticketSubcategories: ticketSubcategories.value,
      ticketStatusOptions: ticketStatusOptions.value,
      ticketPriorityOptions: ticketPriorityOptions.value,
      ticketChannelOptions: ticketChannelOptions.value,
      exploitationZoneOptions: exploitationZoneOptions.value,
      orderTypeOptions: orderTypeOptions.value,
      orderClassOptions: orderClassOptions.value,
    });
  };

  /**
   * Gets the action dropdown values for a given type.
   * @param {string} type - The type of action.
   * @returns {Array} An array of action dropdown values.
   */
  const getActionDropdownValues = type => {
    return getActionOptions({
      agents: agents.value,
      labels: labels.value,
      teams: teams.value,
      slaPolicies: slaPolicies.value,
      languages,
      type,
      addNoneToListFn: addNoneToList,
      priorityOptions: priorityOptions.value,
      // Ticket-specific action options
      ticketStatusOptions: ticketStatusOptions.value,
      ticketPriorityOptions: ticketPriorityOptions.value,
      ticketCategories: ticketCategories.value,
    });
  };

  return {
    booleanFilterOptions,
    statusFilterItems,
    statusFilterOptions,
    priorityOptions,
    messageTypeOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
    // Ticket-specific
    ticketCategories,
    ticketSubcategories,
    ticketStatusOptions,
    ticketPriorityOptions,
    ticketChannelOptions,
    exploitationZoneOptions,
    loadTicketCategories,
  };
}
